import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Security,
  Group,
  AdminPanelSettings
} from '@mui/icons-material'

import { Permission, Role } from '../../types'
import { permissionService } from '../../services/permissionService'
// import { usePermissions } from '../../contexts/PermissionContext'
import { AdminOnly } from '../../components/Permissions/PermissionGuard'
import { AdminButton } from '../../components/Permissions/PermissionButton'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const PermissionsPage: React.FC = () => {
  // const permissions = usePermissions()
  const [tabValue, setTabValue] = useState(0)
  
  // Permissions state
  const [_allPermissions, setAllPermissions] = useState<Permission[]>([])
  const [groupedPermissions, setGroupedPermissions] = useState<Record<string, Permission[]>>({})
  
  // Roles state
  const [roles, setRoles] = useState<Role[]>([])
  
  // Loading and error states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Dialog states
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false)
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  
  // Form states
  const [permissionForm, setPermissionForm] = useState({
    name: '',
    display_name: '',
    description: '',
    resource: '',
    action: ''
  })
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permission_ids: [] as number[]
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [permissionsData, rolesData] = await Promise.all([
        permissionService.getPermissions(),
        permissionService.getRoles()
      ])
      
      setAllPermissions(permissionsData.permissions)
      setGroupedPermissions(permissionsData.grouped)
      setRoles(rolesData)
    } catch (err) {
      setError('Failed to load permissions and roles')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePermission = async () => {
    try {
      await permissionService.createPermission(permissionForm)
      setPermissionDialogOpen(false)
      resetPermissionForm()
      loadData()
    } catch (err) {
      console.error('Failed to create permission:', err)
    }
  }

  const handleUpdatePermission = async () => {
    if (!editingPermission) return
    
    try {
      await permissionService.updatePermission(editingPermission.id, permissionForm)
      setPermissionDialogOpen(false)
      setEditingPermission(null)
      resetPermissionForm()
      loadData()
    } catch (err) {
      console.error('Failed to update permission:', err)
    }
  }

  const handleDeletePermission = async (permission: Permission) => {
    if (!confirm(`Are you sure you want to delete permission "${permission.display_name}"?`)) {
      return
    }
    
    try {
      await permissionService.deletePermission(permission.id)
      loadData()
    } catch (err) {
      console.error('Failed to delete permission:', err)
    }
  }

  const handleCreateRole = async () => {
    try {
      await permissionService.createRole(roleForm)
      setRoleDialogOpen(false)
      resetRoleForm()
      loadData()
    } catch (err) {
      console.error('Failed to create role:', err)
    }
  }

  const handleUpdateRole = async () => {
    if (!editingRole) return
    
    try {
      await permissionService.updateRole(editingRole.id, roleForm)
      setRoleDialogOpen(false)
      setEditingRole(null)
      resetRoleForm()
      loadData()
    } catch (err) {
      console.error('Failed to update role:', err)
    }
  }

  const handleDeleteRole = async (role: Role) => {
    if (role.is_system) {
      alert('Cannot delete system roles')
      return
    }
    
    if (!confirm(`Are you sure you want to delete role "${role.display_name}"?`)) {
      return
    }
    
    try {
      await permissionService.deleteRole(role.id)
      loadData()
    } catch (err) {
      console.error('Failed to delete role:', err)
    }
  }

  const openEditPermission = (permission: Permission) => {
    setEditingPermission(permission)
    setPermissionForm({
      name: permission.name,
      display_name: permission.display_name,
      description: permission.description || '',
      resource: permission.resource,
      action: permission.action
    })
    setPermissionDialogOpen(true)
  }

  const openEditRole = (role: Role) => {
    setEditingRole(role)
    setRoleForm({
      name: role.name,
      display_name: role.display_name,
      description: role.description || '',
      permission_ids: role.permissions?.map(p => p.id) || []
    })
    setRoleDialogOpen(true)
  }

  const resetPermissionForm = () => {
    setPermissionForm({
      name: '',
      display_name: '',
      description: '',
      resource: '',
      action: ''
    })
  }

  const resetRoleForm = () => {
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permission_ids: []
    })
  }

  const handlePermissionToggle = (permissionId: number) => {
    setRoleForm(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }))
  }

  if (loading) {
    return <LoadingSpinner message="Loading permissions..." />
  }

  return (
    <AdminOnly fallback={
      <Alert severity="error">
        You don't have permission to access the admin panel.
      </Alert>
    }>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            <AdminPanelSettings sx={{ mr: 1, verticalAlign: 'middle' }} />
            Permissions & Roles
          </Typography>
        </Box>

        {error && (
          <ErrorMessage message={error} onRetry={loadData} />
        )}

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Permissions" icon={<Security />} />
            <Tab label="Roles" icon={<Group />} />
          </Tabs>
        </Paper>

        {/* Permissions Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">System Permissions</Typography>
            <AdminButton
              variant="contained"
              startIcon={<Add />}
              onClick={() => setPermissionDialogOpen(true)}
            >
              Add Permission
            </AdminButton>
          </Box>

          <Grid container spacing={3}>
            {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
              <Grid item xs={12} md={6} lg={4} key={resource}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {resource.charAt(0).toUpperCase() + resource.slice(1)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {resourcePermissions.map((permission) => (
                        <Box key={permission.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {permission.display_name}
                            </Typography>
                            <Chip label={permission.action} size="small" variant="outlined" />
                          </Box>
                          <Box>
                            <IconButton size="small" onClick={() => openEditPermission(permission)}>
                              <Edit />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeletePermission(permission)}>
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Roles Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">System Roles</Typography>
            <AdminButton
              variant="contained"
              startIcon={<Add />}
              onClick={() => setRoleDialogOpen(true)}
            >
              Add Role
            </AdminButton>
          </Box>

          <Grid container spacing={3}>
            {roles.map((role) => (
              <Grid item xs={12} md={6} lg={4} key={role.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6">{role.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                      </Box>
                      {role.is_system && (
                        <Chip label="System" size="small" color="primary" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {role.permission_count || 0} permissions
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => openEditRole(role)}>
                      Edit
                    </Button>
                    {!role.is_system && (
                      <Button size="small" color="error" onClick={() => handleDeleteRole(role)}>
                        Delete
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Permission Dialog */}
        <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingPermission ? 'Edit Permission' : 'Create Permission'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={permissionForm.name}
                  onChange={(e) => setPermissionForm({ ...permissionForm, name: e.target.value })}
                  placeholder="e.g., transactions.create"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={permissionForm.display_name}
                  onChange={(e) => setPermissionForm({ ...permissionForm, display_name: e.target.value })}
                  placeholder="e.g., Create Transactions"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Resource"
                  value={permissionForm.resource}
                  onChange={(e) => setPermissionForm({ ...permissionForm, resource: e.target.value })}
                  placeholder="e.g., transactions"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={permissionForm.action}
                    onChange={(e) => setPermissionForm({ ...permissionForm, action: e.target.value })}
                    label="Action"
                  >
                    <MenuItem value="create">Create</MenuItem>
                    <MenuItem value="read">Read</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                    <MenuItem value="delete">Delete</MenuItem>
                    <MenuItem value="export">Export</MenuItem>
                    <MenuItem value="manage">Manage</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={permissionForm.description}
                  onChange={(e) => setPermissionForm({ ...permissionForm, description: e.target.value })}
                  placeholder="Optional description of what this permission allows"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={editingPermission ? handleUpdatePermission : handleCreatePermission}
              variant="contained"
            >
              {editingPermission ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role Dialog */}
        <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingRole ? 'Edit Role' : 'Create Role'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  placeholder="e.g., manager"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={roleForm.display_name}
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                  placeholder="e.g., Manager"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  placeholder="Optional description of this role"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
                    <Box key={resource} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {resource.charAt(0).toUpperCase() + resource.slice(1)}
                      </Typography>
                      {resourcePermissions.map((permission) => (
                        <FormControlLabel
                          key={permission.id}
                          control={
                            <Checkbox
                              checked={roleForm.permission_ids.includes(permission.id)}
                              onChange={() => handlePermissionToggle(permission.id)}
                            />
                          }
                          label={permission.display_name}
                          sx={{ display: 'block', ml: 2 }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={editingRole ? handleUpdateRole : handleCreateRole}
              variant="contained"
            >
              {editingRole ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminOnly>
  )
}

export default PermissionsPage